/**
 * ZIP mínimo sem compressão (método STORE).
 * Suficiente para empacotar imagens/vídeos já comprimidos.
 */

const CRC_TABELA = (() => {
  const tabela = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    tabela[i] = c >>> 0;
  }
  return tabela;
})();

export function crc32(dados: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < dados.length; i += 1) {
    c = CRC_TABELA[(c ^ dados[i]!) & 0xff]! ^ (c >>> 8);
  }
  return (c ^ 0xffffffff) >>> 0;
}

export type EntradaZip = {
  readonly nome: string;
  readonly dados: Uint8Array;
};

function escreverU16(alvo: Uint8Array, offset: number, valor: number): void {
  alvo[offset] = valor & 0xff;
  alvo[offset + 1] = (valor >>> 8) & 0xff;
}

function escreverU32(alvo: Uint8Array, offset: number, valor: number): void {
  alvo[offset] = valor & 0xff;
  alvo[offset + 1] = (valor >>> 8) & 0xff;
  alvo[offset + 2] = (valor >>> 16) & 0xff;
  alvo[offset + 3] = (valor >>> 24) & 0xff;
}

function encoderNome(nome: string): Uint8Array {
  return new TextEncoder().encode(nome.replace(/^\/+/, "").replace(/\\/g, "/"));
}

/**
 * Monta bytes de um ZIP com as entradas (sem deflate).
 */
export function montarZipSemCompressao(
  entradas: readonly EntradaZip[],
): Uint8Array {
  if (entradas.length === 0) {
    throw new Error("ZIP sem arquivos");
  }

  const locais: Uint8Array[] = [];
  const centrais: Uint8Array[] = [];
  let offsetLocal = 0;

  for (const entrada of entradas) {
    const nomeBytes = encoderNome(entrada.nome);
    const crc = crc32(entrada.dados);
    const tamanho = entrada.dados.length;

    const local = new Uint8Array(30 + nomeBytes.length + tamanho);
    escreverU32(local, 0, 0x04034b50);
    escreverU16(local, 4, 20);
    escreverU16(local, 6, 0);
    escreverU16(local, 8, 0);
    escreverU16(local, 10, 0);
    escreverU16(local, 12, 0);
    escreverU32(local, 14, crc);
    escreverU32(local, 18, tamanho);
    escreverU32(local, 22, tamanho);
    escreverU16(local, 26, nomeBytes.length);
    escreverU16(local, 28, 0);
    local.set(nomeBytes, 30);
    local.set(entrada.dados, 30 + nomeBytes.length);
    locais.push(local);

    const central = new Uint8Array(46 + nomeBytes.length);
    escreverU32(central, 0, 0x02014b50);
    escreverU16(central, 4, 20);
    escreverU16(central, 6, 20);
    escreverU16(central, 8, 0);
    escreverU16(central, 10, 0);
    escreverU16(central, 12, 0);
    escreverU16(central, 14, 0);
    escreverU32(central, 16, crc);
    escreverU32(central, 20, tamanho);
    escreverU32(central, 24, tamanho);
    escreverU16(central, 28, nomeBytes.length);
    escreverU16(central, 30, 0);
    escreverU16(central, 32, 0);
    escreverU16(central, 34, 0);
    escreverU16(central, 36, 0);
    escreverU32(central, 38, 0);
    escreverU32(central, 42, offsetLocal);
    central.set(nomeBytes, 46);
    centrais.push(central);

    offsetLocal += local.length;
  }

  const tamanhoCentral = centrais.reduce((s, c) => s + c.length, 0);
  const fim = new Uint8Array(22);
  escreverU32(fim, 0, 0x06054b50);
  escreverU16(fim, 4, 0);
  escreverU16(fim, 6, 0);
  escreverU16(fim, 8, entradas.length);
  escreverU16(fim, 10, entradas.length);
  escreverU32(fim, 12, tamanhoCentral);
  escreverU32(fim, 16, offsetLocal);
  escreverU16(fim, 20, 0);

  const total =
    offsetLocal + tamanhoCentral + fim.length;
  const saida = new Uint8Array(total);
  let cursor = 0;
  for (const parte of [...locais, ...centrais, fim]) {
    saida.set(parte, cursor);
    cursor += parte.length;
  }
  return saida;
}
